import { Avatar, Card, Icon, IconButton, Tooltip } from "@/components/atoms";
import { useOperator } from "@/hooks";
import { useAppDispatch } from "@/redux/hooks";
import { setAction, setInlineEditing } from "@/redux/slices/booking/bookingSlice";
import { BookingDetails } from "@/redux/slices/booking/types";
import { formatCurrency, getAssetUrl } from "@/utils";
import { DetailField } from "../../../common/DetailField";

interface DriverSectionProps {
  booking: BookingDetails;
}

export const DriverSection = ({ booking }: DriverSectionProps) => {
  const { driver, metadata, status } = booking;
  const dispatch = useAppDispatch();
  const operator = useOperator();
  const hasDriver = Boolean(driver);
  const isAcceptedFromNetwork = metadata.isAcceptedFromNetwork;

  const isDriverEditAllowed = metadata.isDriverEditAllowed;
  const isDriverPercentageEditable = status !== "closed" && status !== "complete_awaiting_review" && status !== "cancelled";

  const driverAvatarSrc = driver ? getAssetUrl(driver.avatar, { height: 24, width: 24 }) : undefined;
  const driverName = driver?.name ?? "-";
  const driverPhone = driver?.phone ?? "-";
  const ratingAverage = driver?.rating?.average ?? 0;
  const ratingJobCount = driver?.rating?.jobCount ?? "-";

  const handleEditMode = () => {
    dispatch(setInlineEditing("change-driver"));
  };

  const handleAssignmentLog = () => {
    dispatch(setAction("driver-assignment-logs"));
  };

  const handleEditDriverPercentage = () => {
    dispatch(setInlineEditing("driver-percentage"));
  };

  const handleViewDriverProfile = () => {
    if (!driver?.id) return;

    const driverOperatorKey = driver?.operator?.providerKey?.toLowerCase() || "";
    const currentOperatorKey = operator.providerCode?.toLowerCase() || "";

    const href = window.location.href;
    const baseUrl = href.substring(0, href.lastIndexOf("/") + 1);

    const newPath =
      driverOperatorKey === currentOperatorKey
        ? `drivers/my-drivers/${driver.id}/profile`
        : "drivers/offload";


    const newUrl = `${baseUrl}${newPath}`;
    window.open(newUrl, "_blank", "noopener,noreferrer");
  };

  const driverPaymentAmount = hasDriver && booking.cost.breakdown.driverPayment ? booking.cost.breakdown.driverPayment : 0;

  return (
    <Card className="space-y-3 p-2.5">
      <DetailField
        leading={
          <Avatar src={driverAvatarSrc} alt={hasDriver ? driverName : "User"} size="xs">
            {!hasDriver ? <Icon name="User" size="lg" className="text-neutral-dark-gray" /> : null}
          </Avatar>
        }
        label="Driver"
        value={driverName}
        right={
          <div className="flex gap-1">
            {hasDriver && (
              <Tooltip content="View Driver Profile" placement="top">
                <IconButton
                  iconName="User"
                  iconSize="md"
                  size="xs"
                  variant="primary"
                  className="h-8 w-8"
                  onClick={handleViewDriverProfile}
                />
              </Tooltip>
            )}
            {isDriverEditAllowed && (
              <Tooltip content="Update Driver" placement="top">
                <IconButton iconName="Edit2" iconSize="md" size="xs" variant="primary" className="h-8 w-8" onClick={handleEditMode} />
              </Tooltip>
            )}
            {hasDriver && (
              <Tooltip content="View Driver Assignment Logs" placement="top">
                <IconButton
                  iconName="Stickynote"
                  iconSize="md"
                  size="xs"
                  variant="primary"
                  className="h-8 w-8"
                  onClick={handleAssignmentLog}
                />
              </Tooltip>
            )}
          </div>
        }
      />

      <DetailField leading={<Icon name="Call" size="lg" className="text-neutral-dark-gray" />} label="Driver Number" value={driverPhone} />

      <DetailField
        leading={<Icon name="Star1" size="lg" className="text-neutral-dark-gray" />}
        label="Rating"
        value={hasDriver ? `${ratingAverage.toFixed(2)} (${ratingJobCount} Jobs)` : "-"}
      />

      {!isAcceptedFromNetwork && (
        <DetailField
          leading={<Icon name="AddCircle" size="lg" className="text-neutral-dark-gray" />}
          label="Driver Payment %"
          labelInlineRight={
            <Tooltip content="Percentage of the “Base Trip Cost” that the driver will receive" placement="top">
              <Icon name="InfoCircle" className="inline-block translate-x-1 rotate-180 text-neutral-dark-gray" size="sm" />
            </Tooltip>
          }
          value={booking.cost.percentageToDriver ? `${booking.cost.percentageToDriver}%` : "-"}
          right={
            isDriverPercentageEditable ? (
              <Tooltip content="Update Driver Payment %" placement="top">
                <IconButton
                  iconName="Edit2"
                  iconSize="md"
                  size="xs"
                  variant="primary"
                  className="h-8 w-8"
                  onClick={handleEditDriverPercentage}
                />
              </Tooltip>
            ) : undefined
          }
        />
      )}

      <DetailField
        leading={<Icon name="DollarCircle" size="lg" className="text-neutral-dark-gray" />}
        label="Driver Payment"
        value={formatCurrency(driverPaymentAmount, 2, booking.cost.currency)}
      />
    </Card>
  );
};
